/**
 * SLATE design system color tokens for chart visualizations.
 * Supports light/dark mode theming via data-theme attribute.
 */

export interface ChartColors {
  primary: string;
  primaryLight: string;
  negative: string;
  warning: string;
  axis: string;
  grid: string;
  text: string;
  surface: string;
  muted: string;
}

export const CHART_COLORS_LIGHT: ChartColors = {
  primary: "#2DAA9E",
  primaryLight: "#C4ECE7",
  negative: "#F43F5E",
  warning: "#F59E0B",
  axis: "#52525B",
  grid: "#E6E7EA",
  text: "#1B2A45",
  surface: "#FFFFFF",
  muted: "#52525B",
};

export const CHART_COLORS_DARK: ChartColors = {
  primary: "#7BD3CA",
  primaryLight: "#1F7E76",
  negative: "#F43F5E",
  warning: "#F59E0B",
  axis: "#A1A1AA",
  grid: "rgba(255,255,255,0.10)",
  text: "#E4E4E7",
  surface: "#101C30",
  muted: "#A1A1AA",
};

/**
 * Hook to get chart colors based on current theme.
 * Reads data-theme attribute from document.documentElement.
 */
export function useChartColors(): ChartColors {
  const theme =
    typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") : "light";
  return theme === "dark" ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
}

/**
 * Sequential color scale for multi-series charts.
 * Uses SLATE primary + accent colors.
 */
export const SEQUENTIAL_COLORS_LIGHT = [
  "#2DAA9E", // primary
  "#F59E0B", // warning
  "#3B82F6", // blue
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
];

export const SEQUENTIAL_COLORS_DARK = [
  "#7BD3CA", // primary
  "#F59E0B", // warning
  "#60A5FA", // blue
  "#A78BFA", // purple
  "#F472B6", // pink
  "#2DD4BF", // teal
  "#FB923C", // orange
];

/**
 * Get sequential color for multi-series charts by index.
 */
export function getSequentialColor(index: number, theme: "light" | "dark" = "light"): string {
  const colors = theme === "dark" ? SEQUENTIAL_COLORS_DARK : SEQUENTIAL_COLORS_LIGHT;
  return colors[index % colors.length] as string;
}
